import { banks } from '@/data/banks';
import { formatarCPF, formatarMoeda, formatarDataBR } from './masks';
import type { Candidato, RespostasEtapa2Candidato } from '../models';
import { montarEnderecoCompleto } from '../models/candidato.opcoes';

/**
 * Perguntas e respostas do formulário do candidato, prontas pra exibir — usadas pelo
 * "Ver respostas" (VisualizarCandidatoModal) e pelo PDF (respostasCandidatoPdf), pra os dois
 * mostrarem exatamente a mesma coisa.
 */
export interface CampoExibicao {
  label: string;
  valor: string;
}

/** Lista (formulário atual) ou texto (formulário antigo / quem enviou pelo contorno do erro de
 * quando o servidor ainda esperava texto) — sem isso, um registro em texto quebrava esta tela. */
/** Formulário atual: todos informam outro banco (com ou sem Itaú); respostas antigas não têm
 * banco/dígito, e quem tinha Itaú não informava outra conta. */
function contaBancariaLegivel(r: RespostasEtapa2Candidato): string {
  const temOutroBanco = Boolean(r.outroBancoCodigo || r.outroBancoAgencia || r.outroBancoConta || r.outroBancoPix);
  if (r.possuiContaItau && !temOutroBanco) return 'Possui conta no Itaú';
  const prefixo = r.possuiContaItau ? 'Possui conta no Itaú. ' : '';
  const banco = r.outroBancoCodigo
    ? `${r.outroBancoCodigo} - ${banks.find((b) => b.code === r.outroBancoCodigo)?.name ?? ''}`.trim()
    : '—';
  const conta = r.outroBancoDigito ? `${r.outroBancoConta ?? '—'}-${r.outroBancoDigito}` : r.outroBancoConta ?? '—';
  return `${prefixo}Outro banco — ${banco}, Ag. ${r.outroBancoAgencia ?? '—'}, Conta ${conta}, Pix ${r.outroBancoPix || '—'}`;
}

function filhosLegivel(r: RespostasEtapa2Candidato): string {
  const filhos = Array.isArray(r.filhos) ? r.filhos : [];
  return (
    filhos
      .map((f) => {
        const detalhes = [
          f.dataNascimento ? formatarDataBR(f.dataNascimento) : '',
          f.sexo ?? '',
          f.incluirNoPlano ? 'no plano de saúde' : '',
        ].filter(Boolean);
        return `${f.nome} (${detalhes.join(', ')})`;
      })
      .join('; ') || '—'
  );
}

function regimeTrabalhoLegivel(valor: unknown): string {
  if (Array.isArray(valor)) return valor.length > 0 ? valor.join(', ') : '—';
  return typeof valor === 'string' && valor.trim() ? valor : '—';
}

export function montarCampos(candidato: Candidato): CampoExibicao[] {
  const r = candidato.respostas;
  if (!r) return [];
  return [
    { label: 'Data de nascimento', valor: r.dataNascimento ? formatarDataBR(r.dataNascimento) : '—' },
    { label: 'RG', valor: r.rg ?? '—' },
    // Respostas antigas: órgão/UF ficavam na Etapa 1 (hoje estão na Etapa 2).
    ...(r.orgaoEmissorRg ? [{ label: 'Órgão emissor do RG', valor: r.orgaoEmissorRg }] : []),
    ...(r.orgaoEmissorRgUf ? [{ label: 'UF do órgão emissor', valor: r.orgaoEmissorRgUf }] : []),
    ...(r.dataEmissaoRg ? [{ label: 'Data de emissão do RG', valor: formatarDataBR(r.dataEmissaoRg) }] : []),
    { label: 'CPF', valor: r.cpf ? formatarCPF(r.cpf) : '—' },
    {
      label: 'Cidade e Estado de nascimento',
      valor: r.cidadeNascimento ? `${r.cidadeNascimento} - ${r.ufNascimento ?? '—'}` : r.cidadeEstadoNascimento ?? '—',
    },
    { label: 'Cidade e Estado de residência atual', valor: r.cidadeEstadoResidencia ?? '—' },
    { label: 'Nome completo da mãe', valor: r.nomeMae ?? '—' },
    { label: 'Nome completo do pai', valor: r.nomePai ?? '—' },
    // Só aparece se o candidato foi cadastrado com "Incluir perguntas sobre uso de IA".
    ...(candidato.perguntasSobreIA
      ? [
          {
            label: 'Você organiza o seu processo de desenvolvimento com ferramentas de IA em etapas bem definidas?',
            valor: r.sobreIaEtapas ?? '—',
          },
          {
            label:
              'Quais são as etapas que você divide, quando decide incluir a IA em seu processo de desenvolvimento?',
            valor: r.sobreIaProcesso ?? '—',
          },
          {
            label:
              'O que você faz para garantir a qualidade da solução que você gera utilizando IA antes de liberar para ser utilizada?',
            valor: r.sobreIaQualidade ?? '—',
          },
        ]
      : []),
    { label: 'Disponibilidade de início', valor: r.disponibilidadeInicio ?? '—' },
    { label: 'Pretensão Salarial', valor: r.pretensaoSalarial ? formatarMoeda(r.pretensaoSalarial) : '—' },
    { label: 'Autorização (LGPD)', valor: r.autorizacaoLgpd ? 'Autorizado' : 'Não autorizado' },
  ];
}

/** Mesma lógica de `montarCampos`, mas pra Etapa 2 — só os campos sempre presentes ou
 * cujo condicional foi atendido aparecem (evita mostrar "—" pra tudo que não se aplica,
 * tipo CID quando não tem deficiência). */
export function montarCamposEtapa2(candidato: Candidato): CampoExibicao[] {
  const r = candidato.respostasEtapa2;
  if (!r) return [];
  const campos: CampoExibicao[] = [
    { label: 'Gênero', valor: r.genero ?? '—' },
    { label: 'Como se declara', valor: r.raca ?? '—' },
    ...(r.sexo ? [{ label: 'Sexo (conforme documento)', valor: r.sexo }] : []),
    ...(r.orgaoEmissorRg ? [{ label: 'Órgão emissor do RG', valor: r.orgaoEmissorRg }] : []),
    ...(r.orgaoEmissorRgUf ? [{ label: 'UF do órgão emissor', valor: r.orgaoEmissorRgUf }] : []),
    ...(r.dataEmissaoRg ? [{ label: 'Data de emissão do RG', valor: formatarDataBR(r.dataEmissaoRg) }] : []),
    { label: 'Sobrenome para o e-mail da Raro', valor: r.sobrenomeEmail ?? '—' },
    { label: 'Nacionalidade', valor: r.nacionalidade ?? '—' },
    { label: 'Estado civil', valor: r.estadoCivil ?? '—' },
    ...(r.nomeConjuge ? [{ label: 'Nome do cônjuge', valor: r.nomeConjuge }] : []),
    { label: 'Possui deficiência', valor: r.possuiDeficiencia ? `Sim — CID ${r.deficienciaCid ?? '—'}` : 'Não' },
    {
      label: 'Escolaridade',
      valor: r.escolaridadeStatus ? `${r.escolaridade ?? '—'} — ${r.escolaridadeStatus}` : r.escolaridade ?? '—',
    },
    ...(r.escolaridadeCurso ? [{ label: 'Curso', valor: r.escolaridadeCurso }] : []),
    { label: 'Telefone', valor: r.telefone ?? '—' },
    { label: 'E-mail pessoal', valor: r.emailPessoal ?? '—' },
    {
      label: 'Contato de emergência',
      valor: `${r.contatoEmergenciaNome ?? '—'} (${r.contatoEmergenciaParentesco ?? '—'}) — ${r.contatoEmergenciaTelefone ?? '—'}`,
    },
    { label: 'Endereço', valor: (r.enderecoLogradouro ? montarEnderecoCompleto(r) : r.endereco) || '—' },
    ...(r.enderecoComplemento ? [{ label: 'Complemento', valor: r.enderecoComplemento }] : []),
    { label: 'Tipo de contrato', valor: r.tipoContrato ?? '—' },
    ...(r.cnpj ? [{ label: 'CNPJ', valor: r.cnpj }] : []),
    ...(r.razaoSocial ? [{ label: 'Razão social', valor: r.razaoSocial }] : []),
    ...(r.estagioPeriodo ? [{ label: 'Estágio — período', valor: r.estagioPeriodo }] : []),
    ...(r.estagioInstituicao ? [{ label: 'Estágio — instituição', valor: r.estagioInstituicao }] : []),
    { label: 'Conta bancária', valor: contaBancariaLegivel(r) },
    { label: 'Regime de trabalho', valor: regimeTrabalhoLegivel(r.regimeTrabalho) },
    ...(r.linhaOnibusTarifa ? [{ label: 'Linhas de ônibus e tarifa', valor: r.linhaOnibusTarifa }] : []),
    { label: 'Plano de saúde', valor: r.planoSaude ?? '—' },
    ...(r.incluirConjugePlano ? [{ label: 'Cônjuge no plano de saúde', valor: `Sim — ${r.nomeConjuge ?? '—'}` }] : []),
    ...(r.desejaDependentesPlano
      ? [
          {
            label: 'Dependentes do plano de saúde',
            valor: (Array.isArray(r.dependentesPlano) ? r.dependentesPlano : []).map((d) => `${d.nome} (${d.parentesco}, ${d.idade} anos)`).join('; ') || '—',
          },
        ]
      : []),
    { label: 'Pronome', valor: r.pronome ?? '—' },
    ...(r.possuiApelido ? [{ label: 'Apelido', valor: r.apelido ?? '—' }] : []),
    { label: 'Tamanho de camisa', valor: r.tamanhoCamisa ?? '—' },
    { label: 'Número de calçado', valor: r.tamanhoCalcado ?? '—' },
    ...(r.possuiFilhos
      ? [{ label: 'Filhos', valor: filhosLegivel(r) }]
      : []),
    ...(r.possuiAlergia ? [{ label: 'Alergia', valor: r.alergiaDescricao ?? '—' }] : []),
    { label: 'Tipo de alimentação', valor: r.tipoAlimentacao ?? '—' },
    ...(r.possuiRestricaoAlimentar
      ? [{ label: 'Restrição alimentar', valor: r.restricaoAlimentarDescricao ?? '—' }]
      : []),
    { label: 'Comida e bebida preferida', valor: r.comidaBebidaFavorita ?? '—' },
    { label: 'Tempo livre', valor: r.tempoLivre ?? '—' },
    { label: 'Lugar que quer visitar', valor: r.lugarQuerVisitar ?? '—' },
    { label: 'Cor favorita', valor: r.corFavorita ?? '—' },
    { label: 'Livro favorito', valor: r.livroFavorito ?? '—' },
    { label: 'Filme/série favorito', valor: r.filmeFavorito ?? '—' },
    { label: 'Personagem favorito', valor: r.personagemFavorito ?? '—' },
    { label: 'Banda/cantor favorito', valor: r.bandaCantorFavorito ?? '—' },
    { label: 'Música do momento', valor: r.musicaDoMomento ?? '—' },
    { label: 'Pratica esporte', valor: r.praticaEsporte ?? '—' },
    { label: 'Talento especial', valor: r.talentoEspecial ?? '—' },
    { label: 'Meta ou sonho', valor: r.metaSonho ?? '—' },
    ...(r.possuiPet ? [{ label: 'Animal de estimação', valor: r.petDescricao ?? '—' }] : []),
    ...(r.interesseCompartilharFotos
      ? [{ label: 'Autoriza uso de imagens', valor: r.autorizaUsoImagens ? 'Sim' : 'Não' }]
      : []),
  ];
  return campos;
}

export function montarComSeguranca(montar: () => CampoExibicao[]): CampoExibicao[] {
  try {
    return montar();
  } catch (erro) {
    console.error('Falha ao montar respostas do candidato', erro);
    return [{ label: 'Aviso', valor: 'Não foi possível mostrar estas respostas — algum campo está num formato inesperado. Avise o time do Digital.' }];
  }
}

